import type { FormEvent } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { Plus, Save, Trash2 } from "lucide-react"
import { SettingsLayout } from "@/components/settings/SettingsLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { PageProps } from "@/types/inertia"

type FieldDef = {
  key: string
  label: string
  type: "text" | "number" | "select" | "date" | "boolean"
  options?: string[]
}

type CrmFieldsProps = {
  readonly definitions: {
    readonly contacts: readonly FieldDef[]
    readonly accounts: readonly FieldDef[]
    readonly opportunities: readonly FieldDef[]
  }
}

type DefinitionsForm = {
  definitions: {
    contacts: FieldDef[]
    accounts: FieldDef[]
    opportunities: FieldDef[]
  }
}

const ENTITY_LABELS: Record<keyof CrmFieldsProps["definitions"], string> = {
  contacts: "Contacts",
  accounts: "Accounts",
  opportunities: "Opportunities",
}

const TYPE_OPTIONS = ["text", "number", "select", "date", "boolean"]

export default function CrmFields({ definitions }: CrmFieldsProps) {
  const { props } = usePage<PageProps<CrmFieldsProps>>()
  const description = "Manage custom field definitions for contacts, accounts, and opportunities."
  const form = useForm<DefinitionsForm>({
    definitions: {
      contacts: definitions.contacts.map(cloneField),
      accounts: definitions.accounts.map(cloneField),
      opportunities: definitions.opportunities.map(cloneField),
    },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.patch("/settings/crm_fields", { preserveScroll: true })
  }

  const addField = (entity: keyof CrmFieldsProps["definitions"]) => {
    form.setData("definitions", {
      ...form.data.definitions,
      [entity]: [...form.data.definitions[entity], { key: "", label: "", type: "text" as const }],
    })
  }

  const removeField = (entity: keyof CrmFieldsProps["definitions"], index: number) => {
    const updated = form.data.definitions[entity].filter((_, i) => i !== index)
    form.setData("definitions", { ...form.data.definitions, [entity]: updated })
  }

  const updateField = (
    entity: keyof CrmFieldsProps["definitions"],
    index: number,
    patch: Partial<FieldDef>,
  ) => {
    const updated = form.data.definitions[entity].map((field, i) =>
      i === index ? { ...field, ...patch } : field,
    )
    form.setData("definitions", { ...form.data.definitions, [entity]: updated })
  }

  return (
    <>
      <Head title="CRM Field Definitions">
        <meta name="description" content={description} />
        <meta property="og:title" content="CRM Field Definitions" />
        <meta property="og:description" content={description} />
      </Head>
      <SettingsLayout wide>
        <div className="flex flex-col gap-6">
          <header>
            <h1>CRM Field Definitions</h1>
            <p className="mt-2">Define custom fields for contacts, accounts, and opportunities.</p>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}
          {props.flash.alert && (
            <div className="rounded-md border border-danger bg-danger-faded px-4 py-3 text-sm text-danger-display">
              {props.flash.alert}
            </div>
          )}

          <form onSubmit={submit} className="space-y-8">
            {(Object.keys(ENTITY_LABELS) as Array<keyof CrmFieldsProps["definitions"]>).map(
              (entity) => (
                <section
                  key={entity}
                  className="overflow-hidden rounded-lg border border-hairline bg-page"
                >
                  <div className="flex items-center justify-between border-b border-hairline bg-surface px-4 py-3">
                    <h2>{ENTITY_LABELS[entity]}</h2>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => addField(entity)}
                    >
                      <Plus className="h-4 w-4" />
                      Add field
                    </Button>
                  </div>
                  {form.data.definitions[entity].length > 0 ? (
                    <div className="divide-y divide-hairline">
                      <div className="grid grid-cols-[1fr_1fr_140px_120px_40px] gap-3 border-b border-hairline bg-surface px-4 py-2 text-sm text-ink-muted">
                        <div>Key</div>
                        <div>Label</div>
                        <div>Type</div>
                        <div>Options</div>
                        <div />
                      </div>
                      {form.data.definitions[entity].map((field, index) => (
                        <div
                          key={index}
                          className="grid items-center gap-3 px-4 py-2 lg:grid-cols-[1fr_1fr_140px_120px_40px]"
                        >
                          <Input
                            value={field.key}
                            onChange={(event) =>
                              updateField(entity, index, { key: event.target.value })
                            }
                            placeholder="budget_band"
                            aria-label={`${ENTITY_LABELS[entity]} field key ${index + 1}`}
                          />
                          <Input
                            value={field.label}
                            onChange={(event) =>
                              updateField(entity, index, { label: event.target.value })
                            }
                            placeholder="Budget band"
                            aria-label={`${ENTITY_LABELS[entity]} field label ${index + 1}`}
                          />
                          <Select
                            value={field.type}
                            onChange={(event) =>
                              updateField(entity, index, { type: event.target.value as FieldDef["type"] })
                            }
                            aria-label={`${ENTITY_LABELS[entity]} field type ${index + 1}`}
                          >
                            {TYPE_OPTIONS.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </Select>
                          <Input
                            value={(field.options || []).join(", ")}
                            onChange={(event) =>
                              updateField(entity, index, {
                                options: event.target.value
                                  .split(",")
                                  .map((s) => s.trim())
                                  .filter(Boolean),
                              })
                            }
                            placeholder="a, b, c"
                            disabled={field.type !== "select"}
                            aria-label={`${ENTITY_LABELS[entity]} field options ${index + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeField(entity, index)}
                            className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-danger-display"
                            aria-label={`Remove ${ENTITY_LABELS[entity]} field ${index + 1}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-center text-sm text-ink-muted">
                      No custom fields defined for {ENTITY_LABELS[entity].toLowerCase()} yet.
                    </div>
                  )}
                </section>
              ),
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={form.processing}>
                <Save className="h-4 w-4" />
                Save
              </Button>
            </div>
          </form>
        </div>
      </SettingsLayout>
    </>
  )
}

function cloneField(field: FieldDef): FieldDef {
  return { ...field, options: field.options ? [...field.options] : undefined }
}
